/**
 * "Change the SPOC for <client>" — prototype `changeSpoc()`, laid out as a guide §9 pop-up, plus the reason
 * the contract requires. POST /api/v1/clients/{id}/spoc (capability `client.changeSpoc`): closes the open
 * SPOC row, opens the new one, optionally moves open tasks, writes a client note — one transaction.
 *
 * Shared by Clients (client panel), Settings → SPOCs and Team → SPOCs.
 */
import { useMemo, useState } from 'react';
import { SPOC_ELIGIBLE_ROLES, ROLE_LABEL, type Role, type UserRef } from '@amplispot/contracts';
import { ApiFailure } from '../api';
import { newIdempotencyKey, useChangeSpoc, useUsers } from '../lib/data';
import { Field, Modal, errorText, useToast } from '../ui/kit';

export interface SpocTarget {
  id: string;
  name: string;
  spoc: UserRef;
}

export function ChangeSpocModal({ client, onClose }: { client: SpocTarget; onClose: () => void }) {
  const users = useUsers();
  const change = useChangeSpoc();
  const toast = useToast();
  const [key] = useState(newIdempotencyKey);
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [move, setMove] = useState(true);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [formErr, setFormErr] = useState<string | null>(null);

  const eligible = useMemo(
    () =>
      (users.data ?? [])
        .filter((u) => u.status === 'active' && (SPOC_ELIGIBLE_ROLES as readonly Role[]).includes(u.role) && u.id !== client.spoc.id)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [users.data, client.spoc.id],
  );

  const submit = () => {
    const local: Record<string, string> = {};
    if (!userId) local.user_id = 'Pick who takes over this client';
    if (reason.trim().length < 3) local.reason = 'Say why the SPOC is changing, for example "Rebalancing workload"';
    setErrs(local);
    setFormErr(null);
    if (Object.keys(local).length) return;
    change.mutate(
      { clientId: client.id, key, body: { user_id: userId, reason: reason.trim(), move_open_tasks: move } },
      {
        onSuccess: (r) => {
          const who = eligible.find((u) => u.id === userId)?.name ?? 'The new SPOC';
          toast(`${who} is now SPOC for ${client.name}${r.moved_task_count ? `, with ${r.moved_task_count} open task${r.moved_task_count === 1 ? '' : 's'}` : ''}`);
          onClose();
        },
        onError: (e) => {
          if (e instanceof ApiFailure) setErrs(e.fieldErrors);
          setFormErr(errorText(e));
        },
      },
    );
  };

  return (
    <Modal
      title={`Change the SPOC for ${client.name}`}
      sub={`Now ${client.spoc.name}. New tasks for this client go to the SPOC.`}
      onClose={onClose}
      footer={
        <>
          <div className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn pri" onClick={submit} disabled={change.isPending}>
            {change.isPending ? 'Changing…' : 'Change SPOC'}
          </button>
        </>
      }
    >
      {formErr ? <div className="note bad">{formErr}</div> : null}
      <Field label="New SPOC" error={errs.user_id ?? (users.isError ? errorText(users.error) : undefined)}>
        <select className="in" value={userId} onChange={(e) => setUserId(e.target.value)} aria-invalid={!!errs.user_id || undefined} disabled={users.isPending}>
          <option value="">{users.isPending ? 'Loading people…' : eligible.length ? 'Choose a person' : 'No one else can hold a client yet'}</option>
          {eligible.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} · {ROLE_LABEL[u.role]} · {u.client_count} client{u.client_count === 1 ? '' : 's'}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Why is it changing?" error={errs.reason}>
        <textarea className="in" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Rebalancing workload" aria-invalid={!!errs.reason || undefined} />
      </Field>
      <label className="chkrow">
        <input type="checkbox" checked={move} onChange={(e) => setMove(e.target.checked)} /> Move this client’s open tasks too
      </label>
    </Modal>
  );
}
